import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileArrowDown, FilePdf, CalendarBlank, Users } from '@phosphor-icons/react';

export default function ReportsPage() {
  const { api } = useAuth();
  const [staff, setStaff] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedStaff, setSelectedStaff] = useState('');
  const [downloading, setDownloading] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await api.get('/staff');
        setStaff(res.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchStaff();
  }, [api]);

  const downloadPdf = async (endpoint, filename) => {
    setDownloading(filename);
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to generate report. Please try again.');
    }
    setDownloading('');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <p className="overline">Reports</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">PDF Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Download auto-generated household reports</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Weekly Summary */}
        <div className="glass-card p-6 flex flex-col" data-testid="report-weekly">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <CalendarBlank size={24} className="text-primary" weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Weekly Summary</h3>
              <p className="text-xs text-muted-foreground">Staff attendance, expenses, events & tasks</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-4 flex-1">
            Overview of the current week including staff attendance stats, expense breakdown by category, upcoming events, and task completion status.
          </p>
          <button
            onClick={() => downloadPdf('/reports/weekly-summary', `weekly_summary_${new Date().toISOString().slice(0, 10)}.pdf`)}
            disabled={!!downloading}
            data-testid="download-weekly-btn"
            className="w-full py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {downloading === `weekly_summary_${new Date().toISOString().slice(0, 10)}.pdf` ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <><FileArrowDown size={16} /> Download Weekly Report</>
            )}
          </button>
        </div>

        {/* Monthly Expenses */}
        <div className="glass-card p-6 flex flex-col" data-testid="report-monthly">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <FilePdf size={24} className="text-accent" weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Monthly Expenses</h3>
              <p className="text-xs text-muted-foreground">Budget vs actual, all transactions</p>
            </div>
          </div>
          <div className="mb-4">
            <label className="overline block mb-2">Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              data-testid="report-month-input"
              className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30"
            />
          </div>
          <button
            onClick={() => downloadPdf(`/reports/monthly-expenses?month=${selectedMonth}`, `expenses_${selectedMonth}.pdf`)}
            disabled={!!downloading}
            data-testid="download-monthly-btn"
            className="w-full py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 mt-auto"
          >
            {downloading === `expenses_${selectedMonth}.pdf` ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <><FileArrowDown size={16} /> Download Expense Report</>
            )}
          </button>
        </div>

        {/* Salary Slip */}
        <div className="glass-card p-6 flex flex-col" data-testid="report-salary">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Users size={24} className="text-green-600" weight="duotone" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Salary Slip</h3>
              <p className="text-xs text-muted-foreground">Individual staff salary receipt</p>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <div>
              <label className="overline block mb-2">Staff Member</label>
              <select
                value={selectedStaff}
                onChange={e => setSelectedStaff(e.target.value)}
                data-testid="report-staff-select"
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30"
              >
                <option value="">Select staff...</option>
                {staff.map(s => (
                  <option key={s.staff_id} value={s.staff_id}>{s.name} - {s.role}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                data-testid="report-salary-month"
                className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30"
              />
            </div>
          </div>
          <button
            onClick={() => {
              if (!selectedStaff) { alert('Please select a staff member'); return; }
              downloadPdf(`/reports/salary-slip/${selectedStaff}?month=${selectedMonth}`, `salary_${selectedStaff}_${selectedMonth}.pdf`);
            }}
            disabled={!!downloading || !selectedStaff}
            data-testid="download-salary-btn"
            className="w-full py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 mt-auto"
          >
            {downloading.startsWith('salary_') ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <><FileArrowDown size={16} /> Download Salary Slip</>
            )}
          </button>
        </div>
      </div>

      {staff.length === 0 && (
        <div className="rounded-xl border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          No staff members found. Add staff in the Staff Management section to generate salary slips.
        </div>
      )}
    </div>
  );
}
